<script>
	import { nanoid } from "nanoid";
	import Datepicker from "../../../components/Datepicker.svelte";
	import DropZone from "../../../components/DropZone.svelte";
	import FormColumn from "../../../components/FormColumn.svelte";
	import FormRow from "../../../components/FormRow.svelte";
	import Input from "../../../components/Input.svelte";
	import NumberInput from "../../../components/NumberInput.svelte";
	import RestrictedCurrencyInput from "../../../components/RestrictedCurrencyInput.svelte";
	import Select from "../../../components/Select.svelte";
	import AddButton from "../../../components/buttons/AddButton.svelte";
	import DeleteButton from "../../../components/buttons/DeleteButton.svelte";
	import TextButton from "../../../components/buttons/TextButton.svelte";
	import { stringListToOptionList } from "../../../components/lib/helpers";
	import { acceptedFileUploadListSpreadsheet, currencyList, holdingTransactionTypeList, stockCodeList } from "../../../config";
	import HoldingTransaction from "../../../models/HoldingTransaction";

	export let holding;
	export let registrationToken;

	holding.Id = holding.Id || nanoid(6);

	let id = "fileUploader";
	let options = {
		url: "/api/file-upload?token=" + registrationToken,
		acceptedFiles: acceptedFileUploadListSpreadsheet
	};

	const addTransaction = () => {
		holding.HoldingTransactions = [...holding.HoldingTransactions, new HoldingTransaction()];
	};

	const removeTransaction = (index) => {
		holding.HoldingTransactions = holding.HoldingTransactions.filter((_, i) => i !== index);
	};
</script>

<div>
	<FormRow class="mt-2">
		<FormColumn>
			<Input
				bind:value={holding.HoldingName}
				label={"Name of Holding"}
				id={"HoldingName"}
				maxLength={"8000"}
				required />
		</FormColumn>
	</FormRow>
	<FormRow class="mt-2">
		<FormColumn>
			<Input
				bind:value={holding.RegisteredOwner}
				label={"Registered Owner"}
				id={"RegisteredOwner"}
				maxLength={"8000"}
				required />
		</FormColumn>
	</FormRow>
	<FormRow class="mt-2">
		<FormColumn>
			<Input
				bind:value={holding.HoldingIdentificationNumber}
				label={"SRN/HIN (if known)"}
				id={"HoldingIdentificationNumber"}
				maxLength={"25"} />
		</FormColumn>
	</FormRow>
	<FormRow class="mt-2">
		<FormColumn>
			<NumberInput
				bind:value={holding.NumberOfShares}
				label={"How many James Hardie securities did you have as at 20 May 2025?"}
				id={"HoldingNumberOfShares"} />
		</FormColumn>
	</FormRow>
	<FormRow class="align-items-end mt-2">
		<div class="col-md-6">
			<h2>Transactions</h2>
		</div>
		<div class="col-md-6 mt-4 last">
			<TextButton on:click={() => (holding.showFileUpload = !holding.showFileUpload)}>
				{holding.showFileUpload ? "Add transactions manually" : "Upload transaction file"}
			</TextButton>
		</div>
	</FormRow>

	{#if holding.showFileUpload}
		<FormRow>
			<p>If you had multiple transactions you can provide your transaction details using the spreadsheet available
					<a
					href="/documents/JamesHardieTransactionsSpreadsheet.xlsx?t={new Date().getTime()}"
					download>HERE</a
				>  or upload your own transaction listing using the upload box below.
			</p>
			<FormColumn>
				<DropZone
					{options}
					id={"upload_" + holding.Id}
					required
					message="Drop files here or click to upload for this holding"
					bind:documentList={holding.documentList}></DropZone>
			</FormColumn>
		</FormRow>
	{:else}
		{#each holding.HoldingTransactions as transaction, i (transaction)}
			<div class="transaction">
				{#if holding.HoldingTransactions.length > 1}
					<div class="delete">
						<DeleteButton on:delete={() => removeTransaction(i)}></DeleteButton>
					</div>
				{/if}
				<FormRow class="mt-2">
					<FormColumn cols="6">
						<Datepicker
							label="Transaction Date"
							bind:formattedSelected={transaction.TransactionDate}
							start={new Date(2025, 4, 21)}
							end={new Date(2025, 7, 19)}
							defaultHighlighted={new Date(2025, 4, 21)}
							placeholder="e.g. 21/05/2025"
							openOnClass="calendarpop"
							validatorMessage="Please select a date between 21 May 2025 and 19 August 2025"
							required />
					</FormColumn>
					<FormColumn cols="6">
						<Select
							label="Transaction Type"
							bind:value={transaction.TransactionType}
							optionList={stringListToOptionList(holdingTransactionTypeList)}
							textProperty="name"
							valueProperty="value"
							required />
					</FormColumn>
				</FormRow>

				<FormRow class="mt-2">
					<FormColumn cols="3">
						<Select
							label="Stock Code"
							bind:value={transaction.StockCode}
							optionList={stringListToOptionList(stockCodeList)}
							textProperty="name"
							valueProperty="value"
							required
							/>
					</FormColumn>
					<FormColumn cols="3">
						<Select
							label="Currency"
							bind:value={transaction.Currency}
							optionList={stringListToOptionList(currencyList)}
							textProperty="name"
							valueProperty="value"
							required={true}
							/>
					</FormColumn>
				</FormRow>

				<FormRow class="mt-2">
					<FormColumn cols="4">
						<NumberInput 
							label="No. of Securities" 
							bind:value={transaction.NumberOfShares}
							allowDecimal={true}
							required />
					</FormColumn>

					<FormColumn cols="4">
						<RestrictedCurrencyInput
							label="Price per Security"
							currency={transaction.Currency}
							enforceHighLowRestrictions={true}
							stockCode={transaction.StockCode}
							bind:value={transaction.PricePerShare}
							bind:hasPriceError={transaction.hasPriceError}
							transactionDate={transaction.TransactionDate}
							required={true}
						/>
					</FormColumn>
					{#if transaction.hasPriceError}
						<FormColumn cols="12" class="order-md-last">
							<p class="holding-note">Value applied to Price per Security does not fall within the range that securities in James Hardie were traded for this date. Please check the price you are inserting is the accurate average price purchased or sold (i.e. per security) for the transaction and not the total purchase or sale price of all the securities traded.</p>
						</FormColumn>
					{/if}
					<FormColumn cols="4">
						<RestrictedCurrencyInput label="Brokerage" currency={transaction.Currency} bind:value={transaction.Brokerage} required={true}/>
					</FormColumn>
				</FormRow>
			</div>
		{/each}
		<FormRow class="align-items-end mt-2">
			<FormColumn>
				<AddButton on:add={addTransaction}>Add Transaction</AddButton>
			</FormColumn>
		</FormRow>
	{/if}
</div>

<style>
	@media (max-width: 768px) {
		.last {
			text-align: left;
		}
	}

	.transaction {
		position: relative;
		padding: 12px;
		padding-bottom: 22px;
		border-bottom: solid 1px #ededed;
	}

	.transaction:last-child {
		border-bottom: none;
	}

	.transaction .delete {
		position: absolute;
		top: 10px;
		right: 14px;
	}

	.holding-note {
		margin: 0;
		color: var(--color-error);
	}
</style>
